import React from 'react';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '../../context/ThemeContext';

export interface AppIconButtonProps extends Omit<IconButtonProps, 'children'> {
  icon: React.ReactElement;
  tooltip: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'subtle' | 'edit' | 'delete' | 'success' | 'utility' | 'ghost';
}

/**
 * Theme-aware icon button with built-in tooltip.
 *
 * Uses className="nav-button" to opt out of the global button
 * styling in index.css that forces all buttons to sage green.
 */
const AppIconButton: React.FC<AppIconButtonProps> = ({
  icon,
  tooltip,
  variant = 'primary',
  disabled,
  size,
  className,
  sx,
  ...props
}) => {
  const { theme } = useTheme();

  const variantMap = {
    primary: {
      color: theme.button.primary.text,
      bg: theme.button.primary.bg,
      hover: theme.button.primary.hover,
    },
    secondary: {
      color: theme.button.secondary.text,
      bg: theme.button.secondary.bg,
      hover: theme.button.secondary.hover,
    },
    danger: {
      color: theme.button.danger.solidText ?? theme.button.danger.text,
      bg: theme.button.danger.solidBg ?? theme.button.danger.bg,
      hover: theme.button.danger.solidHover ?? theme.button.danger.hover,
    },
    subtle: {
      color: theme.primary,
      bg: `${theme.primary}15`,
      hover: `${theme.primary}25`,
    },
    edit: {
      color: 'var(--text-link)',
      bg: '#DBEAFE',
      hover: '#BFDBFE',
    },
    delete: {
      // Quiet at rest, red on approach. Tokens, not Tailwind red-100/200 —
      // --status-bad-text keeps the glyph readable on the tint (~6.5:1).
      color: 'var(--status-bad-text)',
      bg: 'var(--status-bad-bg)',
      hover: 'var(--status-bad-border)',
    },
    success: {
      color: theme.success || '#059669',
      bg: `${theme.success || '#10B981'}15`,
      hover: `${theme.success || '#10B981'}25`,
    },
    utility: {
      color: theme.gray[600] || '#6B7280',
      bg: theme.button?.utility?.bg || '#F3F4F6',
      hover: theme.gray[300] || '#D1D5DB',
    },
    ghost: {
      color: theme.gray[500] || '#6B7280',
      bg: 'transparent',
      hover: `${theme.gray[200] || '#E5E7EB'}`,
    },
  };

  const v = variantMap[variant];

  // Small size = compact (no min size enforcement)
  const sizeStyles = size === 'small'
    ? { padding: '4px' }
    : { minWidth: 44, minHeight: 44 };

  return (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          aria-label={tooltip}
          disabled={disabled}
          size={size}
          className={`nav-button ${className || ''}`}
          sx={{
            ...sizeStyles,
            color: v.color,
            backgroundColor: v.bg,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: v.hover,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.gray[200],
              color: theme.gray[400],
            },
            ...sx,
          }}
          {...props}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default AppIconButton;
